function ptr(...segments) {
    return segments.map((s) => `/${String(s).replaceAll('~', '~0').replaceAll('/', '~1')}`).join('');
}
/**
 * Choice values for a `select_one` / `select_multiple` / `rank`, whether the
 * list is inline or named. A named list that is not declared resolves to
 * `undefined` rather than `[]`: "no list found" and "a list with no options"
 * are different, and only the second is a change worth reporting.
 */
function choiceValues(el, def) {
    const props = el.props;
    if (Array.isArray(props?.choices))
        return props.choices.map((c) => String(c.value));
    const listName = props?.list;
    if (typeof listName !== 'string')
        return undefined;
    const list = def.choiceLists?.[listName];
    if (!list)
        return undefined;
    return (list.choices ?? []).map((c) => String(c.value));
}
/**
 * Everything about an element except its name, canonicalised.
 *
 * Used for two things: spotting that a removed field and an added field are
 * the same question renamed, and spotting a properties-only change. Key order
 * is normalised so a re-serialised definition does not read as a change.
 */
function shapeOf(el) {
    const { name: _name, ...rest } = el;
    const stable = (value) => {
        if (Array.isArray(value))
            return value.map(stable);
        if (value !== null && typeof value === 'object') {
            const entries = Object.keys(value)
                .sort()
                .map((k) => [k, stable(value[k])]);
            return Object.fromEntries(entries);
        }
        return value;
    };
    // `elements` is excluded: a container's children are compared as fields in
    // their own right, and including them would make every parent group read as
    // changed whenever any descendant changed.
    const { elements: _children, ...withoutChildren } = stable(rest);
    return JSON.stringify(withoutChildren);
}
/**
 * The same, minus the human-facing text.
 *
 * Rename detection needs this. An author who renames `hh_head` to
 * `respondent` almost always updates the label in the same edit — so a
 * pairing rule that demanded identical labels would fail on the common case
 * and succeed only on the rare one, which is the wrong way round.
 */
function semanticShapeOf(el) {
    const stripped = { ...el };
    for (const key of ['label', 'hint', 'guidance', 'requiredMessage', 'constraintMessage']) {
        delete stripped[key];
    }
    return shapeOf(stripped);
}
/**
 * Every named field in the definition, keyed by name.
 *
 * A duplicate name keeps the FIRST occurrence, matching the validator: a
 * duplicate is already an error there, and picking the first keeps the two
 * agreeing about which one they mean while the author fixes it.
 */
export function flattenDefinition(def) {
    const out = new Map();
    const walk = (elements, base, repeatPath) => {
        (elements ?? []).forEach((el, i) => {
            if (!el || typeof el.name !== 'string' || typeof el.type !== 'string')
                return;
            const path = `${base}${ptr(i)}`;
            if (!out.has(el.name)) {
                const field = {
                    name: el.name,
                    type: el.type,
                    path,
                    repeatPath,
                    required: el.required === true,
                    shape: shapeOf(el),
                    semanticShape: semanticShapeOf(el),
                };
                const choices = choiceValues(el, def);
                if (choices !== undefined)
                    field.choices = choices;
                out.set(el.name, field);
            }
            const children = el.elements;
            if (el.type === 'repeat') {
                walk(children, `${path}${ptr('elements')}`, repeatPath === '' ? el.name : `${repeatPath}.${el.name}`);
            }
            else if (el.type === 'group') {
                walk(children, `${path}${ptr('elements')}`, repeatPath);
            }
        });
    };
    (def.pages ?? []).forEach((page, i) => walk(page.elements, ptr('pages', i, 'elements'), ''));
    for (const calc of def.logic?.calculated ?? []) {
        if (!out.has(calc.name)) {
            out.set(calc.name, {
                name: calc.name,
                type: 'calculate',
                path: ptr('logic', 'calculated'),
                repeatPath: '',
                required: false,
                shape: JSON.stringify({ calculate: calc.calculate ?? '' }),
                semanticShape: JSON.stringify({ calculate: calc.calculate ?? '' }),
            });
        }
    }
    return out;
}
/**
 * Is `from → to` a widening, i.e. can every existing answer be carried across
 * mechanically?
 *
 * Deliberately narrow. research/12 row 5 lists int→decimal, date→datetime and
 * select_one→text alongside any→text, and ODK Central allows only →text — so
 * anything beyond this table is a judgement call that belongs to the author,
 * not to a silent automatic step. Being wrong in the permissive direction here
 * means a migration quietly mangles data, which is the failure this whole
 * module exists to prevent.
 */
export function isWideningType(from, to) {
    if (from === to)
        return false;
    if (to === 'text')
        return true;
    if (from === 'number' && to === 'text')
        return true;
    if (from === 'date' && to === 'datetime')
        return true;
    if (from === 'select_one' && to === 'select_multiple')
        return true;
    return false;
}
/** The inverse: a narrowing that cannot be done without possibly losing data. */
export function isNarrowingType(from, to) {
    return from !== to && !isWideningType(from, to);
}
//# sourceMappingURL=flatten.js.map